import React, { useMemo, useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

// ---------------------------------------------------------------------------
// StudentPicker
//
// The adult-facing entry screen for /check-in. The teacher or para taps
// a student, then hands the device over. Every student appears as a big
// tile with their initials — this doubles as the "who's checking in?"
// prompt, so a distractible student sees their own name and doesn't
// tap someone else's tile.
//
// Search is included because a full caseload of 20+ students on one
// screen is scannable but not instant.
// ---------------------------------------------------------------------------

// Consistent tile color for each student based on initials hash.
// Deliberately not random so the same student always shows in the
// same color across days — that's how a young kid learns "my tile is
// the blue one."
function pickTileColor(id) {
  const palette = [
    'bg-blue-500',
    'bg-emerald-500',
    'bg-violet-500',
    'bg-amber-500',
    'bg-rose-500',
    'bg-cyan-500',
    'bg-teal-500',
    'bg-indigo-500',
    'bg-fuchsia-500',
  ];
  let hash = 0;
  const key = String(id || '');
  for (let i = 0; i < key.length; i += 1) {
    hash = (hash * 31 + key.charCodeAt(i)) | 0;
  }
  return palette[Math.abs(hash) % palette.length];
}

function initialsOf(student) {
  const first = (student?.firstName || '').trim();
  const last = (student?.lastName || '').trim();
  if (!first && !last) return '?';
  return `${first[0] || ''}${last[0] || ''}`.toUpperCase() || '?';
}

const StudentPicker = ({ students, onPick }) => {
  const [query, setQuery] = useState('');

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const roster = Array.isArray(students) ? students : [];
    const withNames = roster
      .filter((s) => (s.firstName || s.lastName || '').trim())
      .sort((a, b) => {
        const aStr = `${a.firstName || ''} ${a.lastName || ''}`.trim();
        const bStr = `${b.firstName || ''} ${b.lastName || ''}`.trim();
        return aStr.localeCompare(bStr);
      });
    if (!q) return withNames;
    return withNames.filter((s) => {
      const name = `${s.firstName || ''} ${s.lastName || ''}`.toLowerCase();
      return name.includes(q);
    });
  }, [students, query]);

  if ((students || []).length === 0) {
    return (
      <div className="text-center py-16 bg-white rounded-2xl border border-gray-200 shadow-sm">
        <Icon
          name="Users"
          size={64}
          className="mx-auto mb-4 text-gray-300"
        />
        <h2 className="text-xl font-bold text-gray-900 mb-2">
          No students yet
        </h2>
        <p className="text-gray-600 max-w-md mx-auto">
          Add students to your caseload first, then come back here to run
          check-ins with them.
        </p>
      </div>
    );
  }

  return (
    <div>
      <div className="text-center mb-6">
        <div className="inline-flex items-center gap-2 rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-primary mb-3">
          <Icon name="Hand" size={12} />
          For the student
        </div>
        <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2">
          Who's checking in?
        </h1>
        <p className="text-gray-600 max-w-lg mx-auto">
          Tap your name, then hand the device to the student. They'll
          rate how they did on their goals today.
        </p>
      </div>

      {students.length > 6 && (
        <div className="mb-4">
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search…"
            className="w-full rounded-xl border-2 border-gray-200 bg-white px-4 py-3 text-base focus:outline-none focus:ring-4 focus:ring-primary/20 focus:border-primary"
          />
        </div>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
        {filtered.map((s) => (
          <button
            key={s.id}
            type="button"
            onClick={() => onPick(s.id)}
            className="group flex flex-col items-center gap-2 rounded-2xl bg-white border-2 border-gray-200 p-4 shadow-sm transition-all hover:border-primary hover:shadow-md focus:outline-none focus:ring-4 focus:ring-primary/30 active:scale-95"
            aria-label={`Start check-in for ${s.firstName || ''} ${s.lastName || ''}`.trim()}
          >
            <div
              className={`w-20 h-20 sm:w-24 sm:h-24 rounded-full flex items-center justify-center text-white text-3xl sm:text-4xl font-bold shadow-inner ${pickTileColor(s.id)}`}
            >
              {initialsOf(s)}
            </div>
            <p className="mt-1 text-base sm:text-lg font-semibold text-gray-900 text-center leading-tight">
              {s.firstName || 'Student'}
            </p>
            {s.lastName && (
              <p className="text-xs text-gray-500 -mt-1">
                {s.lastName}
              </p>
            )}
          </button>
        ))}
      </div>

      {filtered.length === 0 && query && (
        <div className="text-center py-8 text-gray-500">
          <Icon name="Search" size={32} className="mx-auto mb-2 opacity-40" />
          <p className="text-sm">
            No student matches "{query}".{' '}
            <button
              type="button"
              onClick={() => setQuery('')}
              className="text-primary hover:underline"
            >
              Clear search
            </button>
          </p>
        </div>
      )}
    </div>
  );
};

export default StudentPicker;
